import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Search,
  Briefcase,
  MapPin,
  DollarSign,
  Clock,
  Calendar,
  ChevronRight,
  Building
} from "lucide-react";

export const API_URL =
  process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const JobCard = ({ job }) => {
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-300 border-b-4 border-transparent hover:border-orange-500">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center mb-2">
            <div className="bg-orange-100 p-2 rounded-full mr-3">
              <Building className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-green-800">{job.title}</h3>
          </div>
          <div className="mt-2 space-y-2">
            <div className="flex items-center text-gray-600">
              <Briefcase className="h-4 w-4 mr-2 text-orange-500" />
              <span>{job.company || 'Internal Position'}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-2 text-orange-500" />
              <span>{job.location}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Clock className="h-4 w-4 mr-2 text-orange-500" />
              <span>{job.jobType}</span>
            </div>
            {job.salary && (
              <div className="flex items-center text-gray-600">
                <DollarSign className="h-4 w-4 mr-2 text-orange-500" />
                <span>{job.salary}</span>
              </div>
            )}
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2 text-orange-500" />
              <span>Posted on {formatDate(job.createdAt)}</span>
            </div>
          </div>
        </div>
        <Link 
          to={`/job/${job._id}`} 
          className="mt-1 inline-flex items-center text-orange-500 hover:text-orange-600"
        >
          <span className="mr-1">View</span>
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-4">
        <p className="text-gray-600 line-clamp-3">{job.description}</p>
      </div>
    </div>
  );
};

const EmployeeJobs = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/jobs`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data.jobs)) {
        const approvedJobs = data.jobs.filter((job) => job.isApproved);
        // Sort jobs by date, newest first
        approvedJobs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setJobs(approvedJobs);
      } else {
        setJobs([]);
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Filtering will happen below in the filteredJobs variable
    setSearchTerm(e.target.value);
  };

  // Filter jobs based on search term
  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (job.company && job.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (job.location && job.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (job.description && job.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-green-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-green-800 mb-4">Internal Job Openings</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Browse through our latest job openings and opportunities for internal transfers and promotions.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Search by job title, company, or location..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-md">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  {error}
                </p>
              </div>
            </div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-md">
            <Briefcase className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No jobs found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchTerm ? "Try adjusting your search terms." : "Check back later for new opportunities."}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredJobs.map((job) => (
              <JobCard key={job._id} job={job} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeJobs; 